import { localDayKey } from '@/utils/userActivity'

/** Per-user, per-browser history for the limited-offer caps. */
export const OFFER_DAY_MS = 24 * 60 * 60 * 1000
export const MAX_IMPRESSIONS = 9
export const MAX_IMPRESSIONS_PER_DAY = 2
export const PAYWALL_CLOSE_MIN_ACCOUNT_AGE_MS = 15 * 60 * 1000
export const RETURN_MIN_ACCOUNT_AGE_MS = OFFER_DAY_MS
export const RETURN_MAX_IMPRESSIONS = 2
export const RETURN_REPEAT_INTERVAL_MS = 7 * OFFER_DAY_MS
const KEY_PREFIX = 'limitedOffer:v1:'

/** `paywall-close` is trigger 1; `return-visit` and `download` are trigger 2. */
export type LimitedOfferTrigger = 'paywall-close' | 'return-visit' | 'download'

export interface LimitedOfferHistory {
  version: 1
  impressions: number
  /** Local calendar day of the latest impression, for the daily cap. */
  dayKey: string | null
  dayImpressions: number
  returnImpressions: number
  lastReturnShownAt: number | null
  acceptedAt: number | null
}

export interface LimitedOfferContext {
  now: number
  /** Account creation time; `null` when the profile does not carry it. */
  accountCreatedAt: number | null
  /** This session started on a different day than the previous visit. */
  returnedToday: boolean
  /** The user has a design or has downloaded something. */
  hasDesignsOrDownloads: boolean
}

export function emptyLimitedOfferHistory(): LimitedOfferHistory {
  return {
    version: 1,
    impressions: 0,
    dayKey: null,
    dayImpressions: 0,
    returnImpressions: 0,
    lastReturnShownAt: null,
    acceptedAt: null,
  }
}

export function limitedOfferHistoryKey(userId: string): string {
  return `${KEY_PREFIX}${encodeURIComponent(userId)}`
}

const isCount = (value: unknown): value is number => Number.isInteger(value) && (value as number) >= 0
const isTimestamp = (value: unknown) => value === null || (typeof value === 'number' && Number.isFinite(value) && value >= 0)
const isDayKey = (value: unknown) => value === null || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value))

/** Malformed history is rejected rather than reset, so it can't reopen the caps. */
export function parseLimitedOfferHistory(raw: string | null): LimitedOfferHistory | null {
  if (raw === null) return emptyLimitedOfferHistory()
  try {
    const value = JSON.parse(raw) as Partial<LimitedOfferHistory> | null
    if (!value || value.version !== 1 ||
      !isCount(value.impressions) || !isCount(value.dayImpressions) || !isCount(value.returnImpressions) ||
      value.dayImpressions > value.impressions || value.returnImpressions > value.impressions ||
      !isDayKey(value.dayKey) ||
      !isTimestamp(value.lastReturnShownAt) || !isTimestamp(value.acceptedAt)) return null
    if ((value.returnImpressions === 0) !== (value.lastReturnShownAt === null)) return null
    return value as LimitedOfferHistory
  } catch {
    return null
  }
}

export function readLimitedOfferHistory(userId: string): LimitedOfferHistory | null {
  try {
    return parseLimitedOfferHistory(localStorage.getItem(limitedOfferHistoryKey(userId)))
  } catch {
    return null
  }
}

export function writeLimitedOfferHistory(userId: string, history: LimitedOfferHistory): boolean {
  try {
    localStorage.setItem(limitedOfferHistoryKey(userId), JSON.stringify(history))
    return true
  } catch {
    return false
  }
}

export function canShowLimitedOffer(
  history: LimitedOfferHistory,
  trigger: LimitedOfferTrigger,
  context: LimitedOfferContext,
): boolean {
  const { now, accountCreatedAt } = context
  if (!Number.isFinite(now) || accountCreatedAt === null || !Number.isFinite(accountCreatedAt)) return false
  if (history.acceptedAt !== null || history.impressions >= MAX_IMPRESSIONS) return false
  if (history.dayKey === localDayKey(now) && history.dayImpressions >= MAX_IMPRESSIONS_PER_DAY) return false
  const accountAge = now - accountCreatedAt
  if (trigger === 'paywall-close') return accountAge >= PAYWALL_CLOSE_MIN_ACCOUNT_AGE_MS
  return accountAge > RETURN_MIN_ACCOUNT_AGE_MS &&
    context.returnedToday &&
    context.hasDesignsOrDownloads &&
    history.returnImpressions < RETURN_MAX_IMPRESSIONS &&
    (history.lastReturnShownAt === null || now - history.lastReturnShownAt >= RETURN_REPEAT_INTERVAL_MS)
}

export function recordLimitedOfferImpression(
  history: LimitedOfferHistory,
  trigger: LimitedOfferTrigger,
  now: number,
): LimitedOfferHistory {
  const today = localDayKey(now)
  const isReturn = trigger !== 'paywall-close'
  return {
    ...history,
    impressions: history.impressions + 1,
    dayKey: today,
    dayImpressions: history.dayKey === today ? history.dayImpressions + 1 : 1,
    returnImpressions: isReturn ? history.returnImpressions + 1 : history.returnImpressions,
    lastReturnShownAt: isReturn ? now : history.lastReturnShownAt,
  }
}

export function recordLimitedOfferAcceptance(history: LimitedOfferHistory, now: number): LimitedOfferHistory {
  return { ...history, acceptedAt: now }
}
